import React, { useEffect, useRef, useState } from 'react'
import { LanguageIcon } from '../icons'
import { Language } from '../data'

type Props = {
    label: string
    showLabel?: boolean
}

const LANGUAGE_STORAGE_KEY = 'language'

const RTL_LANGUAGES: Language[] = ['fa', 'ar']

const languages: { code: Language; name: string; dir?: 'rtl' | 'ltr' }[] = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'sv', name: 'Svenska' },
    { code: 'ar', name: 'العربية', dir: 'rtl' },
    { code: 'fa', name: 'فارسی', dir: 'rtl' },
]

export const LanguageSelector = (props: Props) => {
    const { label, showLabel = false } = props
    const [language, setLanguage] = useState<Language>('en')
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null
        if (stored && languages.some(l => l.code === stored)) {
            setLanguage(stored)
        }
    }, [])

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const selectLanguage = (code: Language) => {
        setLanguage(code)
        localStorage.setItem(LANGUAGE_STORAGE_KEY, code)
        document.documentElement.dir = RTL_LANGUAGES.includes(code) ? 'rtl' : 'ltr'
        document.documentElement.lang = code
        setIsOpen(false)
        window.location.reload()
    }

    const currentName = languages.find(l => l.code === language)?.name ?? 'English'

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                    showLabel ? 'flex items-center gap-3 px-3 py-2 w-full' : 'p-2'
                }`}
                aria-label={label}
                title={label}
            >
                <LanguageIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                {showLabel && (
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                            Language:
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {currentName}
                        </span>
                    </div>
                )}
            </button>
            {isOpen && (
                <div className={`${showLabel ? 'relative mt-1' : 'absolute end-0 mt-2'} w-36 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-1`}>
                    {languages.map(lang => (
                        <button
                            key={lang.code}
                            onClick={() => selectLanguage(lang.code)}
                            className={`w-full ${lang.dir === 'rtl' ? 'text-right' : 'text-left'} px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                                language === lang.code
                                    ? 'font-semibold text-primary-600 dark:text-primary-400'
                                    : 'text-gray-700 dark:text-gray-300'
                            }`}
                        >
                            {lang.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}
